import * as Yup from 'yup';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useFieldArray } from 'react-hook-form';

import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import {
  Card,
  Chip,
  Grid,
  Badge,
  Stack,
  Button,
  Select,
  Switch,
  Divider,
  MenuItem,
  useTheme,
  CardHeader,
  IconButton,
  InputLabel,
  Typography,
  CardContent,
  FormControl,
  useMediaQuery,
  FormHelperText,
  FormControlLabel,
  SelectChangeEvent,
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { getUpdateFieldConfigErrorMessage } from 'src/services/custom-fields/custom-field.utils';
import {
  useGetOptionUsageQuery,
  useGetConfigByEntityTypeQuery,
  useUpdateCustomFieldConfigMutation,
} from 'src/services/custom-fields/custom-field.service';
import {
  CustomFieldType,
  ICustomFieldConfig,
  CustomFieldEntityType,
  UpdateCustomFieldConfigDto,
  CustomFieldOptionRemovalMode,
} from 'src/services/custom-fields/custom-field.types';

import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';
import { ConfirmDialog } from 'src/components/custom-dialog';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import FeatureLockedListView from 'src/components/billing/feature-locked-list-view';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';
import { LoadingCardItem } from 'src/sections/admin/billing/payment/payment-card-item';

import { FeatureType } from 'src/types/subscription.types';

const MAX_FIELDS_PER_ENTITY_TYPE = 5;

export default function CustomFieldSettingsCard() {

  const { t } = useTranslate();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const authContext = useAuthContext();

  const { canAccess } = useFeatures();

  const [selectedEntityType, setSelectedEntityType] = useState<CustomFieldEntityType>(
    CustomFieldEntityType.CONTACT
  );

  const {
    data: configData,
    isFetching: isLoadingConfig,
    refetch,
  } = useGetConfigByEntityTypeQuery({
    entityType: selectedEntityType
  }, {
    skip: !authContext?.organization?.id || !selectedEntityType
  });

  const [updateConfig, { isLoading: isUpdating }] = useUpdateCustomFieldConfigMutation();

  const { data: optionUsage } = useGetOptionUsageQuery(
    { entityType: selectedEntityType },
    { skip: !canAccess(FeatureType.CUSTOM_FIELDS_ENABLED) }
  );

  /** How each dropped option should be retired. Cleared on save and on switching entity type. */
  const [removedOptions, setRemovedOptions] = useState<{ id: string; mode: CustomFieldOptionRemovalMode }[]>([]);

  /** The in-use option awaiting an archive-or-delete decision. */
  const [optionToRemove, setOptionToRemove] = useState<{
    fieldIndex: number; optionIndex: number; id: string; label: string; usage: number;
  } | null>(null);

  const CustomFieldSettingsSchema = Yup.object().shape({
    configs: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().nullable(),
          label: Yup.string().required(t('validation.required')),
          field_type: Yup.string().required(t('validation.required')),
          options: Yup.array().when('field_type', {
            is: CustomFieldType.SELECT,
            then: (schema) =>
              schema
                .of(Yup.string().required(t('validation.required')))
                .min(1, t('validation.min_one_option_required')),
            otherwise: (schema) => schema.nullable(),
          }),
          select_options: Yup.array().when('field_type', {
            is: CustomFieldType.MULTI_SELECT,
            then: (schema) =>
              schema
                .of(
                  Yup.object().shape({
                    id: Yup.string().nullable(),
                    label: Yup.string().required(t('validation.required')),
                  })
                )
                .min(1, t('validation.min_one_option_required')),
            otherwise: (schema) => schema.nullable(),
          }),
          is_required: Yup.boolean().nullable(),
        })
      )
      .max(5, t('validation.max_custom_fields', { max: 5 }))
      .required(t('validation.required')),
  });

  const methods = useForm({
    resolver: yupResolver(CustomFieldSettingsSchema),
    defaultValues: {
      configs: [],
    },
  });

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'configs',
  });

  const watchFieldArray = watch('configs');
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watchFieldArray[index],
  }));

  useEffect(() => {
    if (configData) {
      const sortedData = [...configData].sort((a, b) => moment(a.created_at).diff(moment(b.created_at)));

      setValue(
        'configs',
        sortedData.map((config: ICustomFieldConfig) => ({
          id: config.id,
          label: config.label,
          field_type: config.field_type,
          options: config.options || [],
          // Objects rather than bare strings: the id is what lets a rename keep every stored value
          // pointing at the option, which is the whole reason multi-select fields have option rows.
          select_options: (config.select_options || []).map((option) => ({
            id: option.id,
            label: option.label,
            archived_at: option.archived_at,
          })),
          is_required: config.is_required || false,
        }))
      );
    }
  }, [configData, setValue]);

  const handleEntityTypeChange = (event: SelectChangeEvent<CustomFieldEntityType>) => {
    setSelectedEntityType(event.target.value as CustomFieldEntityType);
    // Pending removals belong to the fields of the type being left. Carrying them across would
    // apply a decision made about one entity's options to another's.
    setRemovedOptions([]);
  };

  const handleAddOption = (fieldIndex: number) => {
    const currentConfigs = [...watchFieldArray];

    if (!currentConfigs[fieldIndex].options) {
      currentConfigs[fieldIndex].options = [];
    }

    currentConfigs[fieldIndex].options?.push('');
    setValue('configs', currentConfigs);

  };

  const handleRemoveOption = (fieldIndex: number, optionIndex: number) => {
    const currentConfigs = [...watchFieldArray];
    if (currentConfigs[fieldIndex] && currentConfigs[fieldIndex].options) {
      currentConfigs[fieldIndex].options?.splice(optionIndex, 1);
      setValue('configs', currentConfigs);
    }
  };

  const handleOptionChange = (fieldIndex: number, optionIndex: number, value: string) => {
    setValue(`configs.${fieldIndex}.options.${optionIndex}`, value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // --- multi-select options (rows with stable ids, unlike SELECT's bare strings) ---

  const handleAddSelectOption = (fieldIndex: number) => {
    const currentConfigs = [...watchFieldArray] as any[];
    if (!currentConfigs[fieldIndex].select_options) currentConfigs[fieldIndex].select_options = [];
    // No id: the server creates the row. Existing options keep theirs, so a rename stays a rename.
    currentConfigs[fieldIndex].select_options.push({ label: '' });
    setValue('configs', currentConfigs, { shouldDirty: true });
  };

  const handleSelectOptionChange = (fieldIndex: number, optionIndex: number, value: string) => {
    setValue(`configs.${fieldIndex}.select_options.${optionIndex}.label`, value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  /**
   * Removing an option is two different acts depending on whether records hold it.
   *
   * An option nobody uses just goes. One that is in use is a decision only the admin can make —
   * keep the data and retire the option, or remove both — so it opens the dialog rather than
   * guessing. An unsaved option has no id and no stored values, so it always just goes.
   */
  const handleRemoveSelectOption = (fieldIndex: number, optionIndex: number) => {
    const option = watchFieldArray[fieldIndex]?.select_options?.[optionIndex];
    const usage = option?.id ? (optionUsage?.[option.id] ?? 0) : 0;

    if (option?.id && usage > 0) {
      setOptionToRemove({ fieldIndex, optionIndex, id: option.id, label: option.label, usage });
      return;
    }

    // Saying DELETE out loud matters: the server archives anything dropped without a mode, so an
    // unused option that the admin removed came back archived instead of gone (JT1-1701 QA).
    dropSelectOption(fieldIndex, optionIndex, option?.id ? CustomFieldOptionRemovalMode.DELETE : undefined);
  };

  /** Takes the option out of the form. `mode` is recorded so the server knows which act it was. */
  const dropSelectOption = (
    fieldIndex: number,
    optionIndex: number,
    mode?: CustomFieldOptionRemovalMode
  ) => {
    const currentConfigs = [...watchFieldArray] as any[];
    const [removed] = currentConfigs[fieldIndex].select_options.splice(optionIndex, 1);
    setValue('configs', currentConfigs, { shouldDirty: true });

    if (removed?.id && mode) {
      setRemovedOptions((prev) => [
        ...prev.filter((entry) => entry.id !== removed.id),
        { id: removed.id, mode },
      ]);
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      const updateData: UpdateCustomFieldConfigDto = {
        configs: data.configs.map((config: any) => ({
          id: config.id || undefined,
          field_type: config.field_type as CustomFieldType,
          label: config.label.trim(),
          options: config.field_type === CustomFieldType.SELECT ? config.options : undefined,
          // An archived option is LEFT OUT of select_options and restated as an archive removal.
          // The server reads an option present in the payload as active again, so resending the
          // archived ones — which the form lists, because records still hold them — brought them
          // back to life on the next unrelated save (JT1-1701 QA).
          select_options: config.field_type === CustomFieldType.MULTI_SELECT
            ? config.select_options
              .filter((option: any) => !option.archived_at)
              .map((option: any) => ({
                id: option.id || undefined,
                label: option.label.trim(),
              }))
            : undefined,
          // Only the removals belonging to this field, plus the options already archived so they
          // stay that way. Anything the server finds dropped without an entry here is archived, so
          // a stale client can never cause data loss by omission.
          removed_options: config.field_type === CustomFieldType.MULTI_SELECT
            ? [
              ...removedOptions.filter((entry) =>
                (configData?.find((c) => c.id === config.id)?.select_options ?? [])
                  .some((option) => option.id === entry.id)),
              ...config.select_options
                .filter((option: any) => option.archived_at && option.id)
                .map((option: any) => ({ id: option.id, mode: CustomFieldOptionRemovalMode.ARCHIVE })),
            ].filter((entry, index, all) => all.findIndex((other) => other.id === entry.id) === index)
            : undefined,
          is_required: config.is_required,
        })),
      };

      await updateConfig({
        entityType: selectedEntityType,
        data: updateData,
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.custom_field_settings.api.update.success'), {
        variant: 'success',
      });

      // The pending removals have been applied; keeping them would re-send them on the next save.
      setRemovedOptions([]);
      refetch();
    } catch (e) {
      enqueueSnackbar(getUpdateFieldConfigErrorMessage(e), {
        variant: 'error',
      });
    }
  });

  const canEditSettings = useMemo(() => {
    if (!authContext?.account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(authContext.account.org_role);
  }, [authContext]);

  const handleAddField = () => {
    append({
      id: null,
      label: '',
      field_type: CustomFieldType.TEXT,
      options: [],
      is_required: false,
    });
  };

  return (
    <FormProvider methods={methods}>
      <Card sx={{ width: '100%', boxShadow: 0 }}>
        <CardHeader
          avatar={
            <Iconify
              icon="mdi:form-textbox"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6">{t('admin-organization.settings.custom_field_settings.title')}</Typography>
          }
        />

        <CardContent>
          <Stack spacing={2} sx={{ px: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="entity-type-select-label">
                    {t('admin-organization.settings.custom_field_settings.entity_type')}
                  </InputLabel>
                  <Select
                    labelId="entity-type-select-label"
                    value={selectedEntityType}
                    label={t('admin-organization.settings.custom_field_settings.entity_type')}
                    onChange={handleEntityTypeChange}
                  >
                    {Object.values(CustomFieldEntityType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(`admin-organization.settings.custom_field_settings.entity_types.${type}`)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {t('admin-organization.settings.custom_field_settings.entity_type_helper')}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              {
                !isLoadingConfig ? (
                  <>
                    {
                      canAccess(FeatureType.CUSTOM_FIELDS_ENABLED) ?
                        <Grid item xs={12}>
                          <Stack direction="column" my={2} mb={4}>
                            <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
                              <Stack direction="row" gap={2} alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {t('admin-organization.settings.custom_field_settings.custom_fields')}
                                </Typography>

                                {fields?.length > 0 && <Badge badgeContent={fields?.length} color="primary" />}
                              </Stack>

                              <IconButton onClick={handleAddField} disabled={!canEditSettings || fields?.length >= MAX_FIELDS_PER_ENTITY_TYPE}>
                                <Iconify icon="formkit:add" />
                              </IconButton>
                            </Stack>

                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {t('admin-organization.settings.custom_field_settings.custom_fields_helper')}
                            </Typography>
                          </Stack>

                          {controlledFields?.length > 0 && (
                            <Stack direction="column" gap={3}>
                              {controlledFields?.map((field, index) => (
                                <Card key={`${index}_${fields.length}`} sx={{ p: 2, boxShadow: 1 }}>
                                  <Stack direction="column" gap={2}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                      <Typography variant="subtitle2">
                                        {field.label || t('admin-organization.settings.custom_field_settings.new_field')}
                                      </Typography>
                                      <IconButton
                                        onClick={() => remove(index)}
                                        color="error"
                                        disabled={!canEditSettings}
                                      >
                                        <Iconify icon="bi:trash" />
                                      </IconButton>
                                    </Stack>

                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={6}>
                                        <RHFTextField
                                          name={`configs.${index}.label`}
                                          label={t('admin-organization.settings.custom_field_settings.field_label')}
                                          disabled={!canEditSettings}
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <RHFSelect
                                          name={`configs.${index}.field_type`}
                                          label={t('admin-organization.settings.custom_field_settings.field_type')}
                                          disabled={!canEditSettings || field.id !== null}
                                        >
                                          {Object.values(CustomFieldType).map((type) => (
                                            <MenuItem key={type} value={type}>
                                              {t(`admin-organization.settings.custom_field_settings.field_types.${type}`)}
                                            </MenuItem>
                                          ))}
                                        </RHFSelect>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <FormControlLabel
                                          control={
                                            <Switch

                                              checked={field.is_required || false}
                                              onChange={(e) => {
                                                const currentConfigs = [...watchFieldArray];
                                                currentConfigs[index].is_required = e.target.checked;
                                                setValue('configs', currentConfigs);
                                              }}
                                              disabled={!canEditSettings || field.id !== null}
                                            />
                                          }
                                          label={t('admin-organization.settings.custom_field_settings.is_required')}
                                        />
                                      </Grid>
                                    </Grid>

                                    {field.field_type === CustomFieldType.SELECT && (
                                      <Box>
                                        <Stack
                                          direction="row"
                                          justifyContent="space-between"
                                          alignItems="center"
                                          mb={1}
                                        >
                                          <Typography variant="body2">
                                            {t('admin-organization.settings.custom_field_settings.options')}
                                          </Typography>
                                          <IconButton
                                            onClick={() => handleAddOption(index)}
                                            size="small"
                                            disabled={!canEditSettings}
                                          >
                                            <Iconify icon="formkit:add" />
                                          </IconButton>
                                        </Stack>

                                        {field.options && field.options.length > 0 ? (
                                          <Stack spacing={1}>
                                            {field.options.map((option, optionIndex) => (
                                              <Stack
                                                key={optionIndex}
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                              >
                                                <RHFTextField
                                                  name={`configs.${index}.options.${optionIndex}`}
                                                  label={t(
                                                    'admin-organization.settings.custom_field_settings.option_value'
                                                  )}
                                                  value={option}
                                                  onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                                                  disabled={!canEditSettings}
                                                  fullWidth
                                                />
                                                <IconButton
                                                  onClick={() => handleRemoveOption(index, optionIndex)}
                                                  color="error"
                                                  disabled={!canEditSettings}
                                                >
                                                  <Iconify icon="bi:trash" />
                                                </IconButton>
                                              </Stack>
                                            ))}
                                          </Stack>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            {t('admin-organization.settings.custom_field_settings.no_options')}
                                          </Typography>
                                        )}

                                        {errors?.configs?.[index]?.options && (
                                          <FormHelperText error>
                                            {errors.configs[index]?.options?.message}
                                          </FormHelperText>
                                        )}

                                        {/*
                                          Single-select options are stored as the text itself, so
                                          renaming one leaves every record that chose it pointing at
                                          a value that no longer exists. Multi-select options are
                                          rows with ids and do not have this problem — admins need
                                          to know which they picked.
                                        */}
                                        <FormHelperText sx={{ mt: 1 }}>
                                          {t('admin-organization.settings.custom_field_settings.select_rename_warning', {
                                            defaultValue:
                                              'Renaming an option here does not update records that already use it. Use a multi-select field if you expect to rename options.',
                                          })}
                                        </FormHelperText>
                                      </Box>
                                    )}

                                    {field.field_type === CustomFieldType.MULTI_SELECT && (
                                      <Box>
                                        <Stack
                                          direction="row"
                                          justifyContent="space-between"
                                          alignItems="center"
                                          mb={1}
                                        >
                                          <Typography variant="body2">
                                            {t('admin-organization.settings.custom_field_settings.options')}
                                          </Typography>
                                          <IconButton
                                            onClick={() => handleAddSelectOption(index)}
                                            size="small"
                                            disabled={!canEditSettings}
                                          >
                                            <Iconify icon="formkit:add" />
                                          </IconButton>
                                        </Stack>

                                        {field.select_options && field.select_options.length > 0 ? (
                                          <Stack spacing={1}>
                                            {field.select_options.map((option: any, optionIndex: number) => {
                                              const usage = option.id ? (optionUsage?.[option.id] ?? 0) : 0;

                                              return (
                                                <Stack
                                                  key={option.id ?? `new-${optionIndex}`}
                                                  direction="row"
                                                  spacing={1}
                                                  alignItems="center"
                                                >
                                                  <RHFTextField
                                                    name={`configs.${index}.select_options.${optionIndex}.label`}
                                                    label={t('admin-organization.settings.custom_field_settings.option_value')}
                                                    value={option.label}
                                                    onChange={(e) => handleSelectOptionChange(index, optionIndex, e.target.value)}
                                                    disabled={!canEditSettings}
                                                    fullWidth
                                                  />
                                                  {usage > 0 && (
                                                    <Chip
                                                      size="small"
                                                      variant="soft"
                                                      label={t('admin-organization.settings.custom_field_settings.option_in_use', {
                                                        count: usage,
                                                        defaultValue: `${usage} in use`,
                                                      })}
                                                    />
                                                  )}
                                                  {/* Archived options stay listed, because records still hold them. Without
                                                      this chip archiving one looked like nothing happened (JT1-1701 QA). */}
                                                  {option.archived_at && (
                                                    <Chip
                                                      size="small"
                                                      variant="soft"
                                                      color="warning"
                                                      label={t('admin-organization.settings.custom_field_settings.option_archived', {
                                                        defaultValue: 'Archived',
                                                      })}
                                                    />
                                                  )}
                                                  <IconButton
                                                    onClick={() => handleRemoveSelectOption(index, optionIndex)}
                                                    color="error"
                                                    disabled={!canEditSettings}
                                                  >
                                                    <Iconify icon="bi:trash" />
                                                  </IconButton>
                                                </Stack>
                                              );
                                            })}
                                          </Stack>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            {t('admin-organization.settings.custom_field_settings.no_options')}
                                          </Typography>
                                        )}

                                        {errors?.configs?.[index]?.select_options && (
                                          <FormHelperText error>
                                            {(errors.configs[index] as any)?.select_options?.message}
                                          </FormHelperText>
                                        )}
                                      </Box>
                                    )}
                                  </Stack>
                                </Card>
                              ))}
                            </Stack>
                          )}

                          {controlledFields?.length === 0 && (
                            <Stack>
                              <EmptyContent
                                title={t('admin-organization.settings.custom_field_settings.no_fields_title')}
                                description={t('admin-organization.settings.custom_field_settings.no_fields_description')}
                              />
                            </Stack>
                          )}
                        </Grid>
                        :
                        (
                          <FeatureLockedListView text={t('billing.subscription_required')} />
                        )
                    }
                  </>

                ) : (
                  <Grid item xs={12}>
                    <Stack spacing={2}>
                      <LoadingCardItem />
                      <LoadingCardItem />
                      <LoadingCardItem />
                    </Stack>
                  </Grid>
                )
              }

            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <LoadingButton
                type="submit"
                onClick={onSubmit}
                variant="contained"
                loading={isSubmitting || isUpdating}
                disabled={!isValid || !canEditSettings || isLoadingConfig || controlledFields.length === 0 || !canAccess(FeatureType.CUSTOM_FIELDS_ENABLED)}
              >
                {t('common.update')}
              </LoadingButton>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/*
        Removing an in-use option is the admin's call, not ours. Archiving keeps the records intact;
        deleting matches how removing a label behaves. The two outcomes are different enough — and
        one of them irreversible enough — that guessing would be wrong either way.
      */}
      <ConfirmDialog
        open={!!optionToRemove}
        // Wider than the default xs: the two outcomes need explaining, and squeezing that into a
        // narrow column is how people stop reading it.
        maxWidth="sm"
        onClose={() => setOptionToRemove(null)}
        title={t('admin-organization.settings.custom_field_settings.remove_option_title', {
          defaultValue: 'Remove option',
        })}
        content={
          <Stack spacing={2}>
            <Typography variant="body2">
              {t('admin-organization.settings.custom_field_settings.remove_option_in_use', {
                label: optionToRemove?.label,
                count: optionToRemove?.usage,
                defaultValue: `"${optionToRemove?.label}" is currently used by ${optionToRemove?.usage} record(s).`,
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('admin-organization.settings.custom_field_settings.remove_option_archive_help', {
                defaultValue:
                  'Archive — the option stops being selectable, but records that already have it keep showing it and you can still filter by it. Nothing is lost.',
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('admin-organization.settings.custom_field_settings.remove_option_delete_help', {
                defaultValue:
                  'Delete — the option is removed from every record and deleted, the same way deleting a label behaves. This cannot be undone.',
              })}
            </Typography>
          </Stack>
        }
        action={
          <>
            <Button
              variant="contained"
              onClick={() => {
                if (optionToRemove) {
                  dropSelectOption(optionToRemove.fieldIndex, optionToRemove.optionIndex, CustomFieldOptionRemovalMode.ARCHIVE);
                }
                setOptionToRemove(null);
              }}
            >
              {t('admin-organization.settings.custom_field_settings.archive_option', { defaultValue: 'Archive' })}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                if (optionToRemove) {
                  dropSelectOption(optionToRemove.fieldIndex, optionToRemove.optionIndex, CustomFieldOptionRemovalMode.DELETE);
                }
                setOptionToRemove(null);
              }}
            >
              {t('common.delete')}
            </Button>
          </>
        }
      />
    </FormProvider>
  );
}